import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { getCompanyOverviewDataV2 } from '../../../store/dashboard/overview/service';
import { getSingleCompanyDetailsService } from '../../../store/investor/service';
import { GOOGLE_FAVICON_HOST_RE, buildLogoSources, findSubjectCompanyWebsite, pickWebsite } from '../../../utils/urlMetadata';

// Ordered logo-source cache. Key: `${investorOrgId}:${assesseeOrgId}` so a logo
// resolved for one investor org is never served to a different org that logs in
// on the same tab (the module stays resident across SPA logout/login).
const logoCache = new Map<string, string[]>();
// Subscribers waiting for a pending fetch, keyed the same way as logoCache.
const subscribers = new Map<string, Set<(sources: string[]) => void>>();

const EMPTY: string[] = [];

/**
 * Unified company-logo hook for any surface: resolves the ordered source chain
 * (direct `logo`/`websiteUrl` → else a cached, deduped fetch by `orgId`) and
 * drives the render-fallback state machine (advance on <img> error; drop a
 * Google favicon that loads as the generic globe at or under
 * `genericFaviconMaxSizePx`) in one place. Callers get back exactly what the
 * badge needs to render: `currentSource`, `handleError`, `handleLoad`.
 */
export function useCompanyLogo(params: { logo?: string; websiteUrl?: string; orgId?: string; genericFaviconMaxSizePx?: number }): {
    currentSource: string | undefined;
    handleError: () => void;
    handleLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
} {
    const { logo, websiteUrl, orgId, genericFaviconMaxSizePx = 16 } = params;

    // 1) Resolve ordered sources: direct (logo/website) when available, else a
    //    cached per-company fetch by orgId.
    const direct = buildLogoSources(logo, websiteUrl);
    const hasDirect = direct.length > 0;
    const fetchOrgId = hasDirect ? undefined : orgId;

    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const cacheKey = fetchOrgId && investorOrgId ? `${investorOrgId}:${fetchOrgId}` : '';

    const [fetched, setFetched] = useState<string[]>(() => (cacheKey && logoCache.has(cacheKey) ? logoCache.get(cacheKey)! : EMPTY));

    useEffect(() => {
        if (!cacheKey) return;

        // Already resolved — use cached value
        if (logoCache.has(cacheKey)) {
            setFetched(logoCache.get(cacheKey)!);
            return;
        }

        // A request is already in-flight — subscribe and wait
        if (subscribers.has(cacheKey)) {
            const subs = subscribers.get(cacheKey)!;
            subs.add(setFetched);
            return () => {
                subs.delete(setFetched);
            };
        }

        // First request for this company — create subscriber set and fetch
        const subs = new Set<(sources: string[]) => void>();
        subs.add(setFetched);
        subscribers.set(cacheKey, subs);

        const finish = (resolved: string[]) => {
            logoCache.set(cacheKey, resolved);
            subscribers.get(cacheKey)?.forEach((cb) => cb(resolved));
            subscribers.delete(cacheKey);
        };

        // 1) Try the base company record first (fast, and what worked before): it
        //    carries a stored logo / website for many companies.
        // 2) Only when that has nothing usable (e.g. websiteUrl === "NA") fall back to
        //    the AI overview's "Target company" competitor website — the exact source
        //    Key Competitors uses — so list rows match the report page.
        // Cached per company, so each is resolved at most once per session.
        getSingleCompanyDetailsService({ assesseeOrgId: fetchOrgId, investorOrgId })
            .then((res: any) => {
                const companyData = res?.data?.portfolioCompanyDetails ?? {};
                const baseDirect = buildLogoSources(companyData?.logo, companyData?.socialInfo?.websiteUrl ?? companyData?.websiteUrl);
                if (baseDirect.length) {
                    finish(baseDirect);
                    return;
                }
                return getCompanyOverviewDataV2({ assesseeOrgId: fetchOrgId }).then((ov: any) => {
                    const competitors: any[] = ov?.data?.responseBody?.data?.competitors ?? [];
                    // Pass the company name so the subject-company match can use it instead
                    // of blindly falling through to competitors[0] (wrong logo).
                    finish(buildLogoSources(undefined, pickWebsite(findSubjectCompanyWebsite(competitors, companyData?.orgName))));
                });
            })
            .catch((err) => {
                console.warn(`useCompanyLogo: failed to resolve logo for org ${fetchOrgId}`, err);
                finish(EMPTY);
            });

        return () => {
            subs.delete(setFetched);
        };
    }, [cacheKey, fetchOrgId, investorOrgId]);

    const sources = hasDirect ? direct : fetched;

    // 2) Render-fallback state machine over the resolved sources: advance on
    //    <img> error, reset to the first source whenever the list changes by
    //    content, and treat a small Google favicon load as a miss.
    const [index, setIndex] = useState(0);

    const sourcesKey = sources.join('|');
    useEffect(() => {
        setIndex(0);
    }, [sourcesKey]);

    const currentSource = sources[index];
    const advance = () => setIndex((i) => i + 1);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        // Google's favicon service returns a 16x16 generic globe when it has no
        // real favicon for the domain. That loads successfully, so onError never
        // fires — detect the tell-tale small globe and fall through to the next
        // source (ultimately the first-letter badge) instead of showing it.
        const img = e.currentTarget;
        const isGoogleFavicon = GOOGLE_FAVICON_HOST_RE.test(currentSource ?? '');
        if (isGoogleFavicon && img.naturalWidth > 0 && img.naturalWidth <= genericFaviconMaxSizePx) {
            advance();
        }
    };

    return { currentSource, handleError: advance, handleLoad };
}
